/**
 * Timeline layout algorithm for the Needs Map
 * Arranges nodes from left to right based on time or sequence
 */

import { Node, Edge } from '@xyflow/react';
import { TopicNode } from '@/types/nodeTypes';
import { TopicEdge } from '@/types/edgeTypes';

/**
 * Apply timeline layout to nodes
 * Arranges nodes from left to right with optimized vertical distribution
 */
export function applyTimelineLayout(
  nodes: Node[],
  edges: Edge[],
  width: number = 1000,
  height: number = 600,
  defaultNodeWidth: number = 250,
  defaultNodeHeight: number = 150,
  horizontalGap: number = 100,  // 水平方向の間隔を増加（50→100）
  verticalGap: number = 80,     // 垂直方向の間隔を明示的に設定
  maxNodesPerColumn: number = 3 // Maximum nodes in a column before staggering
): Node[] {
  if (nodes.length === 0) return [];

  // Calculate number of columns needed
  const numColumns = Math.min(nodes.length, Math.ceil(width / (defaultNodeWidth + horizontalGap)));
  const nodesPerColumn = Math.ceil(nodes.length / numColumns);
  
  // Stagger nodes if there are too many to fit in a simple grid
  const staggered = nodesPerColumn > maxNodesPerColumn;
  
  // Calculate horizontal spacing
  const usableWidth = width - horizontalGap * 2; // 左右のマージンを確保
  const columnWidth = usableWidth / numColumns;
  
  // Calculate vertical spacing for each column
  const positionedNodes: Node[] = [];
  
  for (let i = 0; i < numColumns; i++) {
    // Get nodes for this column
    const startIdx = i * nodesPerColumn;
    const endIdx = Math.min(startIdx + nodesPerColumn, nodes.length);
    const columnNodes = nodes.slice(startIdx, endIdx);
    
    // Calculate vertical spacing for this column
    // 各ノード間に十分な間隔を確保
    const columnHeight = columnNodes.length * defaultNodeHeight + (columnNodes.length - 1) * verticalGap;
    const startY = (height - columnHeight) / 2;
    
    // Calculate x position for this column with increased spacing
    let columnX = horizontalGap + i * (defaultNodeWidth + horizontalGap);
    
    // Stagger columns slightly for visual interest if needed
    if (staggered) {
      columnX += (i % 2) * (defaultNodeWidth * 0.15);
    }
    
    // Position nodes in this column
    columnNodes.forEach((node, nodeIdx) => {
      // Always use defaultNodeWidth/Height for initial layout
      // This ensures consistent spacing regardless of whether width/height is defined in the template
      const nodeWidth: number = defaultNodeWidth;
      const nodeHeight: number = defaultNodeHeight;
      
      // 垂直方向の間隔を考慮した配置
      const y = startY + nodeIdx * (nodeHeight + verticalGap);
      
      // Add some visual interest with a slight vertical offset for alternating columns
      const verticalOffset = staggered ? ((i % 2) * nodeHeight * 0.3) : 0;
      
      positionedNodes.push({
        ...node,
        position: { x: columnX, y: y + verticalOffset },
        // Store size information in node data
        data: {
          ...node.data,
          width: nodeWidth,
          height: nodeHeight
        }
      });
    });
  }
  
  return positionedNodes;
}

/**
 * Apply timeline layout to topic nodes
 */
export function applyTimelineLayoutToTopicNodes(
  nodes: TopicNode[],
  edges: TopicEdge[],
  width: number = 1000,
  height: number = 600,
  defaultNodeWidth: number = 250,
  defaultNodeHeight: number = 150
): TopicNode[] {
  if (nodes.length === 0) return [];

  // Sort nodes by importance (optional, improves layout visual hierarchy)
  const sortedNodes = [...nodes].sort((a, b) => b.importance - a.importance);
  
  // Calculate number of columns and nodes per column
  const horizontalGap = 100; // 水平方向の間隔を増加（50→100）
  const verticalGap = 80;    // 垂直方向の間隔を明示的に設定
  const maxNodesPerColumn = 3;
  const numColumns = Math.min(sortedNodes.length, Math.ceil(width / (defaultNodeWidth + horizontalGap)));
  const nodesPerColumn = Math.ceil(sortedNodes.length / numColumns);
  
  // Stagger nodes if there are too many to fit in a simple grid
  const staggered = nodesPerColumn > maxNodesPerColumn;
  
  // Calculate horizontal spacing
  const usableWidth = width - horizontalGap * 2; // 左右のマージンを確保
  const columnWidth = usableWidth / numColumns;
  
  // Calculate vertical spacing for each column
  const positionedNodes: TopicNode[] = [];
  
  for (let i = 0; i < numColumns; i++) {
    // Get nodes for this column
    const startIdx = i * nodesPerColumn;
    const endIdx = Math.min(startIdx + nodesPerColumn, sortedNodes.length);
    const columnNodes = sortedNodes.slice(startIdx, endIdx);
    
    // Calculate vertical spacing for this column with increased spacing
    const columnHeight = columnNodes.length * defaultNodeHeight + (columnNodes.length - 1) * verticalGap;
    const startY = (height - columnHeight) / 2;
    
    // Calculate x position for this column with increased spacing
    let columnX = horizontalGap + i * (defaultNodeWidth + horizontalGap);
    
    // Stagger columns slightly for visual interest if needed
    if (staggered) {
      columnX += (i % 2) * (defaultNodeWidth * 0.15);
    }
    
    // Position nodes in this column
    columnNodes.forEach((node, nodeIdx) => {
      // Always use defaultNodeWidth/Height for initial layout
      // This ensures consistent spacing regardless of whether width/height is defined in the template
      const nodeWidth: number = defaultNodeWidth;
      const nodeHeight: number = defaultNodeHeight;
      
      // 垂直方向の間隔を考慮した配置
      const y = startY + nodeIdx * (nodeHeight + verticalGap);
      
      // Add some visual interest with a slight vertical offset for alternating columns
      const verticalOffset = staggered ? ((i % 2) * nodeHeight * 0.3) : 0;
      
      positionedNodes.push({
        ...node,
        position: { x: columnX, y: y + verticalOffset },
        // Set consistent width and height
        width: defaultNodeWidth,
        height: defaultNodeHeight
      });
    });
  }
  
  return positionedNodes;
}